/**
 * Feature Usage Analytics Service
 *
 * One typed function per category. Each auto-injects the `category` property
 * so PostHog dashboards can filter by category natively.
 */

import { trackEvent } from '@/analytics';
import {
  FeatureCategory,
  CatalogEventProperties,
  BookingEventProperties,
  ScheduleEventProperties,
  SalesEventProperties,
  BillingEventProperties,
  ClientEventProperties,
  TeamEventProperties,
  StorefrontEventProperties,
  ReferralEventProperties,
  AnalyticsEventProperties,
  NotificationEventProperties,
  SettingsEventProperties,
  AIEventProperties,
  OnboardingEventProperties,
  AuthEventProperties,
  IdeasEventProperties,
  CATALOG_EVENTS,
  BOOKING_EVENTS,
  SCHEDULE_EVENTS,
  SALES_EVENTS,
  BILLING_EVENTS,
  CLIENT_EVENTS,
  TEAM_EVENTS,
  STOREFRONT_EVENTS,
  REFERRAL_EVENTS,
  ANALYTICS_EVENTS,
  NOTIFICATION_EVENTS,
  SETTINGS_EVENTS,
  AI_EVENTS,
  ONBOARDING_EVENTS,
  AUTH_EVENTS,
  IDEAS_EVENTS
} from '@/types/analytics/featureUsage';

// Internal helper — typed at the public function boundaries, loose at the SDK call
const trackFeature = (
  eventName: string,
  category: FeatureCategory,
  properties: Record<string, unknown>
): void => {
  trackEvent(eventName, { ...properties, category });
};

// ============================================================================
// Tracking functions — one per category
// ============================================================================

export const trackCatalogEvent = (
  eventName: (typeof CATALOG_EVENTS)[keyof typeof CATALOG_EVENTS],
  properties: Omit<CatalogEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'catalog', properties);
};

export const trackBookingEvent = (
  eventName: (typeof BOOKING_EVENTS)[keyof typeof BOOKING_EVENTS],
  properties: Omit<BookingEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'booking', properties);
};

export const trackScheduleEvent = (
  eventName: (typeof SCHEDULE_EVENTS)[keyof typeof SCHEDULE_EVENTS],
  properties: Omit<ScheduleEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'schedule', properties);
};

export const trackSalesEvent = (
  eventName: (typeof SALES_EVENTS)[keyof typeof SALES_EVENTS],
  properties: Omit<SalesEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'sales', properties);
};

export const trackBillingEvent = (
  eventName: (typeof BILLING_EVENTS)[keyof typeof BILLING_EVENTS],
  properties: Omit<BillingEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'billing', properties);
};

export const trackClientEvent = (
  eventName: (typeof CLIENT_EVENTS)[keyof typeof CLIENT_EVENTS],
  properties: Omit<ClientEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'clients', properties);
};

export const trackTeamEvent = (
  eventName: (typeof TEAM_EVENTS)[keyof typeof TEAM_EVENTS],
  properties: Omit<TeamEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'team', properties);
};

export const trackStorefrontEvent = (
  eventName: (typeof STOREFRONT_EVENTS)[keyof typeof STOREFRONT_EVENTS],
  properties: Omit<StorefrontEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'storefront', properties);
};

export const trackReferralEvent = (
  eventName: (typeof REFERRAL_EVENTS)[keyof typeof REFERRAL_EVENTS],
  properties: Omit<ReferralEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'referral', properties);
};

export const trackAnalyticsEvent = (
  eventName: (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS],
  properties: Omit<AnalyticsEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'analytics', properties);
};

export const trackNotificationEvent = (
  eventName: (typeof NOTIFICATION_EVENTS)[keyof typeof NOTIFICATION_EVENTS],
  properties: Omit<NotificationEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'notifications', properties);
};

export const trackSettingsEvent = (
  eventName: (typeof SETTINGS_EVENTS)[keyof typeof SETTINGS_EVENTS],
  properties: Omit<SettingsEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'settings', properties);
};

export const trackAIEvent = (
  eventName: (typeof AI_EVENTS)[keyof typeof AI_EVENTS],
  properties: Omit<AIEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'ai', properties);
};

export const trackOnboardingEvent = (
  eventName: (typeof ONBOARDING_EVENTS)[keyof typeof ONBOARDING_EVENTS],
  properties: Omit<OnboardingEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'onboarding', properties);
};

export const trackAuthEvent = (
  eventName: (typeof AUTH_EVENTS)[keyof typeof AUTH_EVENTS],
  properties: Omit<AuthEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'auth', properties);
};

export const trackIdeasEvent = (
  eventName: (typeof IDEAS_EVENTS)[keyof typeof IDEAS_EVENTS],
  properties: Omit<IdeasEventProperties, 'category'>
): void => {
  trackFeature(eventName, 'ideas', properties);
};

// ============================================================================
// Re-export event constants for convenience
// ============================================================================

export {
  CATALOG_EVENTS,
  BOOKING_EVENTS,
  SCHEDULE_EVENTS,
  SALES_EVENTS,
  BILLING_EVENTS,
  CLIENT_EVENTS,
  TEAM_EVENTS,
  STOREFRONT_EVENTS,
  REFERRAL_EVENTS,
  ANALYTICS_EVENTS,
  NOTIFICATION_EVENTS,
  SETTINGS_EVENTS,
  AI_EVENTS,
  ONBOARDING_EVENTS,
  AUTH_EVENTS,
  IDEAS_EVENTS
};
